import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as authService from '../services/auth.service';
import { useAliyunCaptcha } from './useAliyunCaptcha';
import { useTencentCaptcha } from './useTencentCaptcha';
import type { CaptchaSubmission, PublicCaptchaConfig } from '../types/auth.types';

function resolveRuntimeCaptchaError(error: unknown): string {
  if (isApiError(error)) {
    const map: Record<string, string> = {
      network_error: '网络连接失败，请检查网络',
      invalid_response: '验证码配置加载失败，请刷新页面重试',
    };
    return map[error.message] ?? `验证码配置加载失败 (${error.message})`;
  }
  return '验证码配置加载失败，请刷新页面重试';
}

export function useRuntimeCaptcha(onVerified: (submission: CaptchaSubmission) => void) {
  const [config, setConfig] = useState<PublicCaptchaConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    setConfigError(null);
    try {
      const result = await authService.fetchPublicCaptchaConfig();
      if (!['disabled', 'aliyun', 'tencent', 'self_hosted'].includes(result.active_provider)) throw new Error('unsupported captcha provider');
      setConfig(result);
    } catch (error) {
      setConfig(null);
      setConfigError(resolveRuntimeCaptchaError(error));
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const activeProvider = config?.active_provider ?? 'disabled';

  const aliyunCaptcha = useAliyunCaptcha(
    {
      enabled: activeProvider === 'aliyun',
      sceneId: config?.aliyun.scene_id || '',
      prefix: config?.aliyun.prefix || '',
      region: config?.aliyun.region || '',
    },
    (captchaVerifyParam) => onVerified({ captchaVerifyParam }),
  );

  const tencentCaptcha = useTencentCaptcha(
    {
      enabled: activeProvider === 'tencent',
      captchaAppId: config?.tencent.captcha_app_id || 0,
      clientJsUrl: config?.tencent.client_js_url || 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js',
    },
    onVerified,
  );

  const captchaError = useMemo(() => {
    if (configError) {
      return configError;
    }
    if (activeProvider === 'aliyun') {
      return aliyunCaptcha.error;
    }
    if (activeProvider === 'tencent') {
      return tencentCaptcha.error;
    }
    return null;
  }, [activeProvider, aliyunCaptcha.error, configError, tencentCaptcha.error]);

  const ready = useMemo(() => {
    if (configError) {
      return false;
    }
    if (loadingConfig) {
      return false;
    }
    if (activeProvider === 'disabled' || activeProvider === 'self_hosted') {
      return true;
    }
    if (activeProvider === 'aliyun') {
      return aliyunCaptcha.ready;
    }
    if (activeProvider === 'tencent') {
      return tencentCaptcha.ready;
    }
    return false;
  }, [activeProvider, aliyunCaptcha.ready, configError, loadingConfig, tencentCaptcha.ready]);

  const enabled = loadingConfig || Boolean(configError) || activeProvider !== 'disabled';

  const trigger = useCallback(() => {
    if (activeProvider === 'aliyun') {
      aliyunCaptcha.trigger();
      return;
    }
    if (activeProvider === 'tencent') {
      tencentCaptcha.trigger();
    }
  }, [activeProvider, aliyunCaptcha.trigger, tencentCaptcha.trigger]);

  return {
    config,
    activeProvider,
    enabled,
    ready,
    loading: loadingConfig,
    error: captchaError,
    trigger,
    reload: loadConfig,
    captchaElementId: aliyunCaptcha.captchaElementId,
    captchaButtonId: aliyunCaptcha.captchaButtonId,
  };
}
